import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, X, Plus } from 'lucide-react';

const ComparisonContainer = styled.div`
  min-height: 100vh;
  background: #0f0f0f;
  color: #ffffff;
`;

const CompareTop = styled.section`
  background: #0f0f0f;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding: 1rem 0;
  position: sticky;
  top: 0;
  z-index: 100;
  padding-top: 2rem;
`;

const TopContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const TopTitle = styled.h3`
  font-size: 1.8rem;
  font-weight: 600;
  margin: 10px 0;
  color: #ffffff;
  text-align: center;
  @media (max-width: 768px) {
    font-size: 1.3rem;
  }
`;

const AnchorLinks = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    gap: 0.5rem;
  }
`;

const AnchorLink = styled.a`
  color: rgba(255, 255, 255, 0.7);
  text-decoration: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
   font-size: 16px;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    color: #F43F60;
    background: rgba(244, 63, 96, 0.1);
  }
  
  @media (max-width: 768px) {
    padding: 1px 6px;
    font-size: 14px;
  }
`;

const ComparesArea = styled.section`
  padding: 2rem 0;
`;

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const DesktopView = styled.div`
  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileView = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileTabs = styled.ul`
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  overflow-x: auto;
  gap: 0.5rem;
  
  &::-webkit-scrollbar {
    height: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 2px;
  }
`;

const MobileTab = styled.li`
  flex-shrink: 0;
`;

const MobileTabButton = styled.button`
  background: ${props => props.active ? 'rgba(244, 63, 96, 0.2)' : 'transparent'};
  border: 1px solid ${props => props.active ? 'rgba(244, 63, 96, 0.3)' : 'rgba(255, 255, 255, 0.1)'};
  color: ${props => props.active ? '#F43F60' : 'rgba(255, 255, 255, 0.7)'};
  padding: 0.75rem 1rem;
  border-radius: 8px 8px 0 0;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.9rem;
  font-weight: 500;
  white-space: nowrap;
  
  &:hover {
    background: ${props => props.active ? 'rgba(244, 63, 96, 0.3)' : 'rgba(255, 255, 255, 0.1)'};
  }
`;

const MobileTabContent = styled.div`
  display: ${props => props.active ? 'block' : 'none'};
`;

const MobileCompareBox = styled.div`
  background: #444343;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  overflow: hidden;
  margin-top: 1rem;
`;

const MobileAddTab = styled.button`
  background: rgba(244, 63, 96, 0.1);
  border: 1px solid rgba(244, 63, 96, 0.3);
  color: #F43F60;
  padding: 0.75rem 1rem;
  border-radius: 8px 8px 0 0;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.9rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    background: rgba(244, 63, 96, 0.2);
  }
`;

const MobileAddContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 300px;
  border: 2px dashed rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  margin-top: 1rem;
  transition: all 0.2s ease;
  cursor: pointer;
  
  &:hover {
    border-color: #F43F60;
    background: rgba(244, 63, 96, 0.05);
  }
`;

const MobileAddLink = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: rgba(255, 255, 255, 0.7);
  font-size: 1rem;
  font-weight: 500;
  
  &:hover {
    color: #F43F60;
  }
`;

const CompareRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  margin-bottom: 2rem;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }
  
  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const CompareBox = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: rgba(244, 63, 96, 0.3);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  }
`;

const CompareCar = styled.div`
  padding: 1rem;
  text-align: center;
  position: relative;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }
`;

const RemoveButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(244, 63, 96, 0.5);
  border: 1px solid rgba(244, 63, 96, 0.3);
  color: #fff;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(244, 63, 96, 0.8);
    border-color: rgba(244, 63, 96, 0.5);
  }
`;

const CarImage = styled.div`
  height: 150px;
  margin-bottom: 1rem;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 8px;
  }
`;

const CarTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
  color: #ffffff;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin: 1rem 0;
`;

const CompareSection = styled.div`
  padding: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  
  &:last-child {
    border-bottom: none;
  }
`;

const SectionTitle = styled.h5`
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
  color: #F43F60;
`;

const CompareItem = styled.div`
  margin-bottom: 0.75rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ItemLabel = styled.p`
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.7);
  margin: 0 0 0.25rem 0;
`;

const ItemValue = styled.h4`
  font-size: 0.9rem;
  font-weight: 500;
  color: #ffffff;
  margin: 0;
`;

const AddNewCar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  border: 2px dashed rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  transition: all 0.2s ease;
  cursor: pointer;
  
  &:hover {
    border-color: #F43F60;
    background: rgba(244, 63, 96, 0.05);
  }
`;

const AddNewCarLink = styled.a`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: rgba(255, 255, 255, 0.7);
  text-decoration: none;
  font-size: 1rem;
  font-weight: 500;
  
  &:hover {
    color: #F43F60;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.7);
`;

const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  text-align: center;
  color: #F43F60;
`;

const BackButton = styled.button`
  position: fixed;
  top: 2rem;
  left: 2rem;
  background: rgba(244, 63, 96, 0.2);
  border: 1px solid rgba(244, 63, 96, 0.3);
  color: #F43F60;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  z-index: 1000;
  
  &:hover {
    background: rgba(244, 63, 96, 0.3);
    border-color: rgba(244, 63, 96, 0.5);
  }
  
  @media (max-width: 768px) {
    top: 1rem;
    left: 1rem;
    padding: 0.5rem 0.75rem;
    font-size: 0.9rem;
  }
`;

const CarComparison = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    const fetchCars = async () => {
      try {
        const carIds = searchParams.get('cars');
        if (!carIds) {
          setError('No cars selected for comparison');
          setLoading(false);
          return;
        }

        const ids = carIds.split(',');
        
        // Fetch car details for each ID
        const carPromises = ids.map(async (id) => {
          try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/inventory/vehicles/${id}`,{
              headers: {
                'Authorization': `Bearer ${sessionStorage.getItem('token')}`
              }
            });
            const data = await response.json();
            if (data.status === 'success') {
              const vehicle = data.data.vehicle;
              
              // Parse imagelist if it exists
              if (vehicle.imagelist) {
                try {
                  // Try parsing as JSON first
                  try {
                    vehicle.images = JSON.parse(vehicle.imagelist);
                  } catch {
                    // If JSON parse fails, treat as CSV
                    vehicle.images = vehicle.imagelist.split(',').map(url => ({ url: url.trim() }));
                  }
                } catch (err) {
                  console.error('Error parsing imagelist:', err);
                  vehicle.images = [];
                }
              } else {
                vehicle.images = [];
              }
              
              return vehicle;
            } else {
              throw new Error(`Failed to fetch car ${id}`);
            }
          } catch (err) {
            console.error(`Error fetching car ${id}:`, err);
            return null;
          }
        });

        const carResults = await Promise.all(carPromises);
        const validCars = carResults.filter(car => car !== null);
        
        if (validCars.length === 0) {
          setError('No valid cars found for comparison');
        } else {
          setCars(validCars);
        }
      } catch (err) {
        console.error('Error fetching cars:', err);
        setError('Failed to load cars for comparison');
      } finally {
        setLoading(false);
      }
    };

    fetchCars();
  }, [searchParams]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleRemoveCar = (carId) => {
    const compareList = sessionStorage.getItem('compareList');
    if (compareList) {
      const newCompareList = JSON.parse(compareList).filter(car => car != carId);
      sessionStorage.setItem('compareList', JSON.stringify(newCompareList));
    }
    setCars(prev => prev.filter(car => car.id !== carId));
    
    // Adjust active tab if needed
    if (activeTab >= cars.length - 1) {
      setActiveTab(Math.max(0, cars.length - 2));
    }
  };

  const handleCarClick = (carId) => {
    navigate(`/car-details?id=${carId}`);
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const renderCarSections = (car) => (
    <>
      <CompareSection id="pricing">
        <SectionTitle>Pricing</SectionTitle>
        <CompareItem>
          <ItemLabel>Price</ItemLabel>
          <ItemValue>${car.price?.toLocaleString() || car.special_price?.toLocaleString() || 'N/A'}</ItemValue>
        </CompareItem>
        <CompareItem>
          <ItemLabel>MSRP</ItemLabel>
          <ItemValue>${car.msrp?.toLocaleString() || 'N/A'}</ItemValue>
        </CompareItem>
      </CompareSection>

      <CompareSection id="features">
        <SectionTitle>Features</SectionTitle>
        <CompareItem>
          <ItemLabel>Transmission</ItemLabel>
          <ItemValue>{car.transmission || 'N/A'}</ItemValue>
        </CompareItem>
        <CompareItem>
          <ItemLabel>Drivetrain</ItemLabel>
          <ItemValue>{car.drivetrain || 'N/A'}</ItemValue>
        </CompareItem>
        <CompareItem>
          <ItemLabel>Status</ItemLabel>
          <ItemValue>
            {car.new_used || 'N/A'}
            {car.certified && car.certified !== 'non-certified' && ' • Certified'}
          </ItemValue>
        </CompareItem>
      </CompareSection>

      <CompareSection id="specifications">
        <SectionTitle>Specifications</SectionTitle>
        <CompareItem>
          <ItemLabel>Engine</ItemLabel>
          <ItemValue>{car.engine || 'N/A'}</ItemValue>
        </CompareItem>
        <CompareItem>
          <ItemLabel>Stock No</ItemLabel>
          <ItemValue>{car.stock_no || 'N/A'}</ItemValue>
        </CompareItem>
        <CompareItem>
          <ItemLabel>VIN</ItemLabel>
          <ItemValue>{car.vin || 'N/A'}</ItemValue>
        </CompareItem>
        <CompareItem>
          <ItemLabel>Body Style</ItemLabel>
          <ItemValue>{car.body_style || 'N/A'}</ItemValue>
        </CompareItem>
        <CompareItem>
          <ItemLabel>Trim</ItemLabel>
          <ItemValue>{car.trim || 'N/A'}</ItemValue>
        </CompareItem>
      </CompareSection>

      <CompareSection id="fuel-economy">
        <SectionTitle>Fuel Economy</SectionTitle>
        <CompareItem>
          <ItemLabel>MPG City</ItemLabel>
          <ItemValue>{car.mpg_city || 'N/A'}</ItemValue>
        </CompareItem>
        <CompareItem>
          <ItemLabel>MPG Highway</ItemLabel>
          <ItemValue>{car.mpg || 'N/A'}</ItemValue>
        </CompareItem>
        <CompareItem>
          <ItemLabel>Fuel Type</ItemLabel>
          <ItemValue>{car.fuel || 'N/A'}</ItemValue>
        </CompareItem>
      </CompareSection>

      <CompareSection id="colors">
        <SectionTitle>Colors</SectionTitle>
        <CompareItem>
          <ItemLabel>Exterior Color</ItemLabel>
          <ItemValue>{car.ext_color || 'N/A'}</ItemValue>
        </CompareItem>
        <CompareItem>
          <ItemLabel>Interior Color</ItemLabel>
          <ItemValue>{car.int_color || 'N/A'}</ItemValue>
        </CompareItem>
      </CompareSection>

      <CompareSection id="mileage">
        <SectionTitle>Mileage</SectionTitle>
        <CompareItem>
          <ItemLabel>Current Mileage</ItemLabel>
          <ItemValue>{car.mileage?.toLocaleString()} miles</ItemValue>
        </CompareItem>
        <CompareItem>
          <ItemLabel>Website Views</ItemLabel>
          <ItemValue>{car.website_views || 0}</ItemValue>
        </CompareItem>
      </CompareSection>

      <CompareSection id="dealer">
        <SectionTitle>Dealer</SectionTitle>
        <CompareItem>
          <ItemLabel>Dealer Name</ItemLabel>
          <ItemValue>{car.first_name} {car.last_name}</ItemValue>
        </CompareItem>
        <CompareItem>
          <ItemLabel>Location</ItemLabel>
          <ItemValue>{car.city || 'N/A'}</ItemValue>
        </CompareItem>
        <CompareItem>
          <ItemLabel>Phone</ItemLabel>
          <ItemValue>{car.dealer_phone_number || 'N/A'}</ItemValue>
        </CompareItem>
        <CompareItem>
          <ItemLabel>Email</ItemLabel>
          <ItemValue>{car.email || 'N/A'}</ItemValue>
        </CompareItem>
      </CompareSection>
    </>
  );

  if (loading) {
    return (
      <ComparisonContainer>
        <LoadingContainer>
          Loading cars for comparison...
        </LoadingContainer>
      </ComparisonContainer>
    );
  }

  if (error) {
    return (
      <ComparisonContainer>
        <BackButton onClick={handleBack}>
          <ArrowLeft size={16} />
          Back
        </BackButton>
        <ErrorContainer>
          <div>
            <h2>Error</h2>
            <p>{error}</p>
          </div>
        </ErrorContainer>
      </ComparisonContainer>
    );
  }

  return (
    <ComparisonContainer>
    
      <CompareTop>
        <TopContent>
          <TopTitle>Compare Cars</TopTitle>
          <AnchorLinks>
            <AnchorLink href="#pricing" onClick={(e) => { e.preventDefault(); scrollToSection('pricing'); }}>
              Pricing
            </AnchorLink>
            <AnchorLink href="#features" onClick={(e) => { e.preventDefault(); scrollToSection('features'); }}>
              Features
            </AnchorLink>
            <AnchorLink href="#specifications" onClick={(e) => { e.preventDefault(); scrollToSection('specifications'); }}>
              Specifications
            </AnchorLink>
            <AnchorLink href="#fuel-economy" onClick={(e) => { e.preventDefault(); scrollToSection('fuel-economy'); }}>
              Fuel Economy
            </AnchorLink>
            <AnchorLink href="#colors" onClick={(e) => { e.preventDefault(); scrollToSection('colors'); }}>
              Colors
            </AnchorLink>
            <AnchorLink href="#mileage" onClick={(e) => { e.preventDefault(); scrollToSection('mileage'); }}>
              Mileage
            </AnchorLink>
            <AnchorLink href="#dealer" onClick={(e) => { e.preventDefault(); scrollToSection('dealer'); }}>
              Dealer
            </AnchorLink>
          </AnchorLinks>
        </TopContent>
      </CompareTop>

      <ComparesArea>
        <Content>
          <DesktopView>
            <CompareRow>
              {cars.map((car, index) => (
                <CompareBox
                  key={car.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                >
                  <CompareCar onClick={() => handleCarClick(car.id)}>
                    <RemoveButton onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveCar(car.id);
                    }}>
                      <X size={16} />
                    </RemoveButton>
                    <CarImage>
                      <img 
                        src={car.images?.[0]?.url || '/placeholder-car.jpg'} 
                        alt={`${car.make} ${car.model}`}
                        onError={(e) => {
                          e.target.src = '/placeholder-car.jpg';
                        }}
                      />
                    </CarImage>
                    <CarTitle>{car.year} {car.make} {car.model}</CarTitle>
                  </CompareCar>

                  <CompareSection id="pricing">
                    <SectionTitle>Pricing</SectionTitle>
                    <CompareItem>
                      <ItemLabel>Price</ItemLabel>
                      <ItemValue>${car.price?.toLocaleString() || car.special_price?.toLocaleString() || 'N/A'}</ItemValue>
                    </CompareItem>
                    <CompareItem>
                      <ItemLabel>MSRP</ItemLabel>
                      <ItemValue>${car.msrp?.toLocaleString() || 'N/A'}</ItemValue>
                    </CompareItem>
                  </CompareSection>

                  <CompareSection id="features">
                    <SectionTitle>Features</SectionTitle>
                    <CompareItem>
                      <ItemLabel>Transmission</ItemLabel>
                      <ItemValue>{car.transmission || 'N/A'}</ItemValue>
                    </CompareItem>
                    <CompareItem>
                      <ItemLabel>Drivetrain</ItemLabel>
                      <ItemValue>{car.drivetrain || 'N/A'}</ItemValue>
                    </CompareItem>
                    <CompareItem>
                      <ItemLabel>Status</ItemLabel>
                      <ItemValue>
                        {car.new_used || 'N/A'}
                        {car.certified && car.certified !== 'non-certified' && ' • Certified'}
                      </ItemValue>
                    </CompareItem>
                  </CompareSection>

                  <CompareSection id="specifications">
                    <SectionTitle>Specifications</SectionTitle>
                    <CompareItem>
                      <ItemLabel>Engine</ItemLabel>
                      <ItemValue>{car.engine || 'N/A'}</ItemValue>
                    </CompareItem>
                    <CompareItem>
                      <ItemLabel>Stock No</ItemLabel>
                      <ItemValue>{car.stock_no || 'N/A'}</ItemValue>
                    </CompareItem>
                    <CompareItem>
                      <ItemLabel>VIN</ItemLabel>
                      <ItemValue>{car.vin || 'N/A'}</ItemValue>
                    </CompareItem>
                    <CompareItem>
                      <ItemLabel>Body Style</ItemLabel>
                      <ItemValue>{car.body_style || 'N/A'}</ItemValue>
                    </CompareItem>
                    <CompareItem>
                      <ItemLabel>Trim</ItemLabel>
                      <ItemValue>{car.trim || 'N/A'}</ItemValue>
                    </CompareItem>
                  </CompareSection>

                  <CompareSection id="fuel-economy">
                    <SectionTitle>Fuel Economy</SectionTitle>
                    <CompareItem>
                      <ItemLabel>MPG City</ItemLabel>
                      <ItemValue>{car.mpg_city || 'N/A'}</ItemValue>
                    </CompareItem>
                    <CompareItem>
                      <ItemLabel>MPG Highway</ItemLabel>
                      <ItemValue>{car.mpg || 'N/A'}</ItemValue>
                    </CompareItem>
                    <CompareItem>
                      <ItemLabel>Fuel Type</ItemLabel>
                      <ItemValue>{car.fuel || 'N/A'}</ItemValue>
                    </CompareItem>
                  </CompareSection>

                  <CompareSection id="colors">
                    <SectionTitle>Colors</SectionTitle>
                    <CompareItem>
                      <ItemLabel>Exterior Color</ItemLabel>
                      <ItemValue>{car.ext_color || 'N/A'}</ItemValue>
                    </CompareItem>
                    <CompareItem>
                      <ItemLabel>Interior Color</ItemLabel>
                      <ItemValue>{car.int_color || 'N/A'}</ItemValue>
                    </CompareItem>
                  </CompareSection>

                  <CompareSection id="mileage">
                    <SectionTitle>Mileage</SectionTitle>
                    <CompareItem>
                      <ItemLabel>Current Mileage</ItemLabel>
                      <ItemValue>{car.mileage?.toLocaleString()} miles</ItemValue>
                    </CompareItem>
                    <CompareItem>
                      <ItemLabel>Website Views</ItemLabel>
                      <ItemValue>{car.website_views || 0}</ItemValue>
                    </CompareItem>
                  </CompareSection>

                  <CompareSection id="dealer">
                    <SectionTitle>Dealer</SectionTitle>
                    <CompareItem>
                      <ItemLabel>Dealer Name</ItemLabel>
                      <ItemValue>{car.first_name} {car.last_name}</ItemValue>
                    </CompareItem>
                    <CompareItem>
                      <ItemLabel>Location</ItemLabel>
                      <ItemValue>{car.city || 'N/A'}</ItemValue>
                    </CompareItem>
                    <CompareItem>
                      <ItemLabel>Phone</ItemLabel>
                      <ItemValue>{car.dealer_phone_number || 'N/A'}</ItemValue>
                    </CompareItem>
                    <CompareItem>
                      <ItemLabel>Email</ItemLabel>
                      <ItemValue>{car.email || 'N/A'}</ItemValue>
                    </CompareItem>
                  </CompareSection>
                </CompareBox>
              ))}

              {/* { (
                <CompareBox
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: cars.length * 0.1 }}
                >
                  <AddNewCar onClick={() => navigate(-1)}>
                    <AddNewCarLink>
                      <Plus size={20} />
                      Add a Vehicle
                    </AddNewCarLink>
                  </AddNewCar>
                </CompareBox>
              )} */}
            </CompareRow>
          </DesktopView>

          <MobileView>
            <MobileTabs>
              {cars.map((car, index) => (
                <MobileTab key={car._id}>
                  <MobileTabButton 
                    active={index === activeTab} 
                    onClick={() => setActiveTab(index)}
                  >
                    {car.year} {car.make} {car.model}
                  </MobileTabButton>
                </MobileTab>
              ))}
              {/* { (
                <MobileTab>
                  <MobileAddTab onClick={() => navigate(-1)}>
                    <Plus size={16} />
                    Add Vehicle
                  </MobileAddTab>
                </MobileTab>
              )} */}
            </MobileTabs>

            {cars.map((car, index) => (
              <MobileTabContent
                key={car.id}
                active={index === activeTab}
              >
                <MobileCompareBox>
                  <CompareCar onClick={() => handleCarClick(car.id)}>
                    <RemoveButton onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveCar(car.id);
                    }}>
                      <X size={16} />
                    </RemoveButton>
                    <CarImage>
                      <img 
                        src={car.images?.[0]?.url || '/placeholder-car.jpg'} 
                        alt={`${car.make} ${car.model}`}
                        onError={(e) => {
                          e.target.src = '/placeholder-car.jpg';
                        }}
                      />
                    </CarImage>
                    <CarTitle>{car.year} {car.make} {car.model}</CarTitle>
                    <Divider />
                  </CompareCar>

                  {renderCarSections(car)}
                </MobileCompareBox>
              </MobileTabContent>
            ))}

            {/* { (
              <MobileTabContent active={cars.length === activeTab}>
                <MobileAddContent onClick={() => navigate(-1)}>
                  <MobileAddLink>
                    <Plus size={20} />
                    Add a Vehicle
                  </MobileAddLink>
                </MobileAddContent>
              </MobileTabContent>
            )} */}
          </MobileView>
        </Content>
      </ComparesArea>
    </ComparisonContainer>
  );
};

export default CarComparison; 